import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (!token) { toast.error("Missing reset token"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12 bg-grid">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Lock className="w-4 h-4" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">ResumeLock</span>
        </Link>
        <Card className="p-8 shadow-sm">
          {done ? (
            <div className="text-center" data-testid="reset-done-state">
              <div className="w-12 h-12 mx-auto rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">Password updated</h1>
              <p className="text-sm text-slate-500 mt-2">Redirecting you to login…</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight">Choose a new password</h1>
              <p className="text-sm text-slate-500 mt-1">Pick something at least 8 characters long.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="pw" className="text-sm">New password</Label>
                  <Input id="pw" type="password" required value={password}
                         onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                         className="mt-1.5" data-testid="reset-password-input" />
                </div>
                <div>
                  <Label htmlFor="cpw" className="text-sm">Confirm password</Label>
                  <Input id="cpw" type="password" required value={confirm}
                         onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••"
                         className="mt-1.5" data-testid="reset-confirm-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 h-11" data-testid="reset-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
